/**
 * Raw tickers whose AMOUNT is already a fiat value — the list behind priorities 2
 * and 3 of the backend `FiatValueService` (own leg is fiat → the amount IS the
 * value; counterparty leg is fiat → the other side's amount is this leg's value).
 *
 * ⚠️⚠️ WHY IT LIVES IN `shared`. Three readers have to answer «is this leg valued
 * without a market quote?» and they must answer it identically:
 *
 *   - the service itself, which produces the number every report prints;
 *   - the tax-report pre-flight `MISSING_PRICES` check, which transcribes the
 *     service's priority chain into SQL (`validation/missing-price-check.ts`);
 *   - the «Sin precio» badge on the transactions grid
 *     (`resolveLegPriceBadge`, `utils/leg-price-badge.ts`).
 *
 * With the list typed in the backend only, the badge could not reach it, so it
 * trusted the enricher's `priceConfidence = 'missing'` stamp over the fiat
 * counterparty: «7 NEAR bought for 270 EUR» wore «Sin precio» — and blocked the
 * report — while the report valued the purchase at 270 EUR. One list, imported
 * by all three, makes that disagreement impossible to reintroduce by editing
 * one copy.
 *
 * NOTE this is deliberately NOT the backend `FiatCurrencyRegistry`: that registry
 * is DB-driven (`assets WHERE type='fiat'`) and includes stablecoins for tax
 * classification, whereas the priority-2/3 shortcuts key off the RAW ticker only.
 * The catalog-driven fiat check happens later and separately, inside the
 * service's market-price step.
 *
 * ⚠️ Y NO CONTIENE ESTABLES, a propósito y para siempre: un estable no es una
 * divisa cuyo importe SEA su valor, es un activo con un anclaje que el mercado
 * puede romper. Su valoración vive en el paso de precio de mercado, DESPUÉS de
 * consultar el precio histórico, para que una vela real de un día de desanclaje
 * mande sobre el anclaje (`stablecoin-peg.ts`, #2146).
 */
export const FIAT_VALUE_SYMBOL_LIST = [
  'USD',
  'EUR',
  'GBP',
  'CAD',
  'AUD',
  'JPY',
  'CHF',
  'CNY',
  'KRW',
  'MXN',
  'BRL',
  'ARS',
] as const

/** The same list as a set, for membership tests. */
export const FIAT_VALUE_SYMBOLS: ReadonlySet<string> = new Set<string>(FIAT_VALUE_SYMBOL_LIST)

/**
 * The fiat code a RAW ticker stands for, or `null` when its amount is not a fiat
 * value by itself.
 *
 * Compared the way the service and the SQL compare it: upper-cased, NOT trimmed
 * (the SQL reads `UPPER(<leg>_asset_raw)`, and a padded ticker is a different
 * ticker to every other reader too). `null`/`undefined`/`''` are "no ticker".
 */
export function fiatValueSymbolOf(ticker: string | null | undefined): string | null {
  if (ticker === null || ticker === undefined || ticker === '') return null
  const upper = ticker.toUpperCase()
  return FIAT_VALUE_SYMBOLS.has(upper) ? upper : null
}
